import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { toast } from 'sonner';
import { MoreHorizontal, Plus, RefreshCw } from 'lucide-react';
import { P } from '@vagusx/shared';
import type { WaitlistEntry } from '@vagusx/clients';
import { Route, type WaitlistView } from '@/routes/_authenticated/waitlist';
import { TablePage } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useHasPermission } from '@/hooks/use-permissions';
import { useRefreshWaitlist, useWaitlist } from '@/hooks/use-waitlist';
import { formatDate, formatDateTime } from '@/lib/format';
import { WaitlistEntrySheet, AddToWaitlistSheet } from './WaitlistEntrySheet';
import {
  formatDays,
  formatTimeRange,
  PRIORITY_LABEL,
  STATUS_LABEL,
  isOpenStatus,
} from './waitlist-format';

const PRIORITY_VARIANT = { high: 'destructive', normal: 'secondary', low: 'outline' } as const;

// `table-fixed` shares the width between columns, so each cell must clip its
// own text (`truncate`) or it spills into the next column (DataTable clips the
// text headers). The short columns get a fixed width; the rest split what's
// left. Lower-value columns drop out on narrower screens instead of squeezing
// everything.
const HIDE_BELOW_MD = 'hidden md:table-cell';
const HIDE_BELOW_LG = 'hidden lg:table-cell';
const HIDE_BELOW_XL = 'hidden xl:table-cell';

const columns: ColumnDef<WaitlistEntry>[] = [
  {
    id: 'patient',
    header: 'Patient',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{row.original.patient.name}</div>
        {row.original.patient.dateOfBirth && (
          <div className="truncate text-xs text-muted-foreground">
            DOB {formatDate(row.original.patient.dateOfBirth)}
          </div>
        )}
      </div>
    ),
  },
  {
    id: 'appointment',
    header: 'Current appointment',
    meta: { className: HIDE_BELOW_LG },
    cell: ({ row }) => {
      const appt = row.original.appointment;
      if (!appt) return <span className="text-muted-foreground">None</span>;
      return (
        <div className="min-w-0">
          <div className="truncate">{formatDateTime(appt.startTime)}</div>
          {appt.providerName && (
            <div className="truncate text-xs text-muted-foreground">{appt.providerName}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'wants',
    header: 'Wants',
    cell: ({ row }) => {
      const e = row.original;
      return (
        <div className="min-w-0">
          <div className="truncate">{e.appointmentType?.name ?? 'Any appointment type'}</div>
          <div className="truncate text-xs text-muted-foreground">
            {e.provider?.name ?? 'Any provider'} · {e.location?.name ?? 'Any location'}
          </div>
        </div>
      );
    },
  },
  {
    id: 'when',
    header: 'When',
    meta: { className: HIDE_BELOW_MD },
    cell: ({ row }) => {
      const { preferredDaysOfWeek, earliestTime, latestTime } = row.original.preferences;
      return (
        <div className="min-w-0">
          <div className="truncate">{formatDays(preferredDaysOfWeek)}</div>
          <div className="truncate text-xs text-muted-foreground">
            {formatTimeRange(earliestTime, latestTime)}
          </div>
        </div>
      );
    },
  },
  {
    id: 'priority',
    header: 'Priority',
    meta: { headerClassName: 'w-24' },
    cell: ({ row }) => (
      <Badge variant={PRIORITY_VARIANT[row.original.priority]}>
        {PRIORITY_LABEL[row.original.priority]}
      </Badge>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    meta: { className: HIDE_BELOW_MD, headerClassName: 'w-32' },
    cell: ({ row }) => {
      const e = row.original;
      return (
        <div className={isOpenStatus(e.status) ? 'truncate' : 'truncate text-muted-foreground'}>
          {STATUS_LABEL[e.status]}
        </div>
      );
    },
  },
  {
    id: 'waiting',
    header: 'Waiting since',
    meta: { className: HIDE_BELOW_XL, headerClassName: 'w-32' },
    cell: ({ row }) => formatDate(row.original.createdAt),
  },
];

export default function WaitlistPage(): React.ReactElement {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const view: WaitlistView = search.view ?? 'open';
  const canEdit = useHasPermission(P.APPOINTMENTS_EDIT);

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearch = useDeferredValue(searchTerm);

  const { data, isLoading, error } = useWaitlist({
    view,
    search: deferredSearch || undefined,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const refresh = useRefreshWaitlist();

  const setSearch = useCallback(
    (next: { id?: string; view?: WaitlistView }) =>
      navigate({ to: '/waitlist', search: { view: search.view, ...next }, replace: true }),
    [navigate, search.view]
  );

  const handleRefresh = async (): Promise<void> => {
    try {
      const result = await refresh.mutateAsync();
      const changes = result.created + result.updated + result.removed;
      toast.success(
        changes === 0
          ? `Up to date with the EHR (${result.listed} entries)`
          : `Updated from the EHR: ${result.created} new, ${result.updated} changed, ${result.removed} removed`
      );
      if (result.unresolved > 0) {
        toast.warning(
          `${result.unresolved} EHR entries couldn't be matched to a patient, provider, location or type here`
        );
      }
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const viewTabs = useMemo(
    () => (
      <Tabs
        value={view}
        onValueChange={(v) => {
          setPagination((p) => ({ ...p, pageIndex: 0 }));
          setSearch({ view: v as WaitlistView, id: undefined });
        }}
      >
        <TabsList>
          <TabsTrigger value="open">On the list</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>
    ),
    [view, setSearch]
  );

  return (
    <TablePage
      title="Waitlist"
      description="Patients who want an appointment sooner than one is open"
      columns={columns}
      data={data?.items ?? []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      searchPlaceholder="Search by patient name..."
      onSearchChange={(value) => {
        setSearchTerm(value);
        setPagination((p) => ({ ...p, pageIndex: 0 }));
      }}
      searchValue={searchTerm}
      filterBar={viewTabs}
      pagination={pagination}
      onPaginationChange={setPagination}
      onRowClick={(entry) => setSearch({ id: entry.id })}
      emptyMessage={view === 'open' ? 'Nobody is on the waitlist.' : 'No entries.'}
      errorMessage="Failed to load the waitlist. Please try again."
      tableClassName="table-fixed"
      headerAction={
        canEdit ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setSearch({ id: 'new' })}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add to waitlist
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" aria-label="More actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleRefresh} disabled={refresh.isPending}>
                  <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                  Refresh from EHR
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : undefined
      }
    >
      {search.id === 'new' && (
        <AddToWaitlistSheet
          onClose={() => setSearch({ id: undefined })}
          onAdded={(id) => setSearch({ id })}
        />
      )}
      {search.id && search.id !== 'new' && (
        <WaitlistEntrySheet
          key={search.id}
          entryId={search.id}
          canEdit={canEdit}
          onClose={() => setSearch({ id: undefined })}
        />
      )}
    </TablePage>
  );
}
